// src/lib/mfa.js — Thin wrappers around Supabase authenticator-app (TOTP) MFA.
//
// Used for the "vault" step-up gate: before showing crown-jewel data (client
// compensation, bank/wire details), the OS asks for a fresh 6-digit code from
// the team member's authenticator app (the same Google Authenticator they
// already use). Recovery from a lost device is admin-reset (see api/mfa-admin-reset.js).
//
// Supabase MFA reference: https://supabase.com/docs/guides/auth/auth-mfa

import { supabase } from './supabase'

// List the caller's enrolled TOTP factors. Returns [] on error.
export async function listTotpFactors() {
  try {
    const { data, error } = await supabase.auth.mfa.listFactors()
    if (error) return []
    // Only verified TOTP factors count as usable for step-up.
    return (data?.totp || []).filter(f => f.status === 'verified')
  } catch { return [] }
}

export async function hasVerifiedFactor() {
  return (await listTotpFactors()).length > 0
}

// Begin enrollment. Returns { factorId, qrCode, secret, uri } or { error }.
// qrCode is an SVG data URI the user scans; secret is the manual-entry fallback.
export async function startEnrollment(friendlyName = 'Chatrone') {
  try {
    const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp', friendlyName })
    if (error) return { error: error.message }
    return {
      factorId: data.id,
      qrCode:   data.totp?.qr_code || null,
      secret:   data.totp?.secret || null,
      uri:      data.totp?.uri || null,
    }
  } catch (e) { return { error: e.message || 'Enrollment failed' } }
}

// Confirm enrollment with the first code from the app. Marks the factor verified.
export async function confirmEnrollment(factorId, code) {
  try {
    const ch = await supabase.auth.mfa.challenge({ factorId })
    if (ch.error) return { error: ch.error.message }
    const v = await supabase.auth.mfa.verify({ factorId, challengeId: ch.data.id, code: String(code).trim() })
    if (v.error) return { error: v.error.message }
    return { ok: true }
  } catch (e) { return { error: e.message || 'Verification failed' } }
}

// Step-up: challenge an existing verified factor and verify the entered code.
// On success the session is elevated to AAL2. Returns { ok } or { error }.
export async function verifyStepUp(code) {
  try {
    const factors = await listTotpFactors()
    if (!factors.length) return { error: 'no_factor' }
    const factorId = factors[0].id
    const ch = await supabase.auth.mfa.challenge({ factorId })
    if (ch.error) return { error: ch.error.message }
    const v = await supabase.auth.mfa.verify({ factorId, challengeId: ch.data.id, code: String(code).trim() })
    if (v.error) return { error: v.error.message }
    return { ok: true }
  } catch (e) { return { error: e.message || 'Verification failed' } }
}

// Remove a factor (used when re-enrolling). Caller must be the factor owner.
export async function unenrollFactor(factorId) {
  try {
    const { error } = await supabase.auth.mfa.unenroll({ factorId })
    if (error) return { error: error.message }
    return { ok: true }
  } catch (e) { return { error: e.message || 'Unenroll failed' } }
}
